import { Link } from "react-router-dom";
import { Handshake, ArrowLeft } from "lucide-react";

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="mb-10">
    <h2 className="text-2xl font-semibold mb-4">{title}</h2>
    <div className="space-y-3 text-gray-300 leading-relaxed">{children}</div>
  </section>
);

const CollaborationTerms = () => {
  const lastUpdated = "September 3, 2026";

  return (
    <div className="min-h-screen bg-black text-white">
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to Metro City Groove
        </Link>

        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Handshake className="w-10 h-10 text-purple-500" />
            <h1 className="text-4xl md:text-5xl font-bold">Collaboration &amp; Work Terms</h1>
          </div>
          <p className="text-sm text-gray-400">Last Updated: {lastUpdated}</p>
        </div>

        <p className="text-lg text-gray-300 leading-relaxed mb-10">
          These terms govern how work is booked, delivered, paid for and disputed on Metro City Groove — between venues
          and creators, between creators collaborating with each other, and between agencies and the talent they
          represent. They apply in addition to the{" "}
          <Link to="/terms" className="text-purple-400 hover:text-purple-300">Terms of Service</Link> and the{" "}
          <Link to="/payments-policy" className="text-purple-400 hover:text-purple-300">Payments &amp; Payouts Policy</Link>.
        </p>

        <Section title="1. Our role">
          <p>
            Metro City Groove is a marketplace and toolset. We are not an employer, agent, promoter or party to any
            booking. Every engagement is a direct agreement between the parties involved. We provide discovery,
            messaging, contracts-by-record, payment rails and dispute tooling only.
          </p>
        </Section>

        <Section title="2. Booking a creator or venue">
          <ul className="list-disc pl-5 space-y-2">
            <li>A booking request must state the date, call time, set or service length, location, fee and any age restrictions.</li>
            <li>A booking becomes binding when the receiving party accepts it in the app.</li>
            <li>Any change to date, fee, scope or line-up must be agreed in writing in the same conversation or booking thread.</li>
            <li>Verified Pro accounts may publish events, sell tickets and receive bookings; free accounts may not.</li>
          </ul>
        </Section>

        <Section title="3. Fees, deposits and payment timing">
          <ul className="list-disc pl-5 space-y-2">
            <li>Fees are quoted and paid in ZAR through the platform so that both parties keep an auditable record.</li>
            <li>Where a deposit is agreed, it secures the date and is non-refundable if the performer or supplier cancels late without cause.</li>
            <li>The balance becomes payable on completion of the agreed work, unless the booking states otherwise.</li>
            <li>Payment released to a wallet becomes withdrawable under the payout rules in the Payments &amp; Payouts Policy.</li>
            <li>Off-platform payment arrangements are at the parties' own risk and are not covered by our dispute process.</li>
          </ul>
        </Section>

        <Section title="4. Splitting collaboration payments">
          <ul className="list-disc pl-5 space-y-2">
            <li>Collaborators must agree the split (percentage or fixed amount per person) before the work starts.</li>
            <li>The lead creator or event owner receiving the funds is responsible for paying collaborators the agreed share promptly.</li>
            <li>Where an event has both a venue and a hosting creator, ticket income is attributed to the event owner recorded on the event, and scanning rights may be delegated to the venue door team.</li>
            <li>Unrecorded verbal splits cannot be enforced by us; only the split captured in the booking or event record is evidence.</li>
          </ul>
        </Section>

        <Section title="5. Cancellation and no-shows">
          <ul className="list-disc pl-5 space-y-2">
            <li>Cancellation more than 14 days before the date: deposit refundable unless otherwise agreed.</li>
            <li>Cancellation 3–14 days before: deposit forfeited to the non-cancelling party.</li>
            <li>Cancellation within 72 hours, or a no-show: the full agreed fee may be claimed by the non-cancelling party.</li>
            <li>Force majeure (load shedding beyond backup capacity, severe weather, government restriction, death or serious illness) suspends these penalties; parties must reschedule in good faith.</li>
            <li>Repeated cancellations or no-shows affect account standing and may lead to suspension.</li>
          </ul>
        </Section>

        <Section title="6. Standards of work and conduct">
          <ul className="list-disc pl-5 space-y-2">
            <li>Deliver the agreed service professionally, on time, sober and fit to perform.</li>
            <li>Venues must provide safe access, the agreed technical setup and lawful operating conditions.</li>
            <li>Harassment, discrimination, intimidation and unsafe conduct are prohibited and are grounds for immediate removal from the platform.</li>
            <li>Age-restricted events must be enforced at the door by the organiser.</li>
          </ul>
        </Section>

        <Section title="7. Intellectual property and content">
          <ul className="list-disc pl-5 space-y-2">
            <li>Creators retain ownership of their music, artwork, portfolio and performance content.</li>
            <li>Uploading content grants Metro City Groove a licence to host and display it for the operation and promotion of the platform.</li>
            <li>Recording, streaming or commercial re-use of a performance requires the performer's written permission.</li>
            <li>You must hold the rights to everything you upload, including samples and third-party recordings, and must clear any performance licensing required by law.</li>
          </ul>
        </Section>

        <Section title="8. Independent contractor status">
          <p>
            Nothing on the platform creates employment, partnership, joint venture or agency between users, or between a
            user and Metro City Groove. Each party manages its own tax, statutory contributions, insurance and permits.
          </p>
        </Section>

        <Section title="9. Liability">
          <ul className="list-disc pl-5 space-y-2">
            <li>Each party is liable for loss, injury or damage it causes at a booking, including damage to equipment and premises.</li>
            <li>Venues are responsible for crowd safety, licensing and compliance at their premises; organisers are responsible for the event they publish.</li>
            <li>Metro City Groove is not liable for cancelled events, unpaid fees between users, performance quality, injury, theft, or indirect or consequential loss arising from any engagement.</li>
            <li>Our total liability for any claim relating to the platform is limited to the platform fees you paid us in the three months before the claim.</li>
            <li>Users indemnify Metro City Trading (Pty) Ltd against claims brought by third parties arising from their events, content or conduct.</li>
            <li>Public liability insurance is strongly recommended for venues and for creators using their own equipment.</li>
          </ul>
        </Section>

        <Section title="10. Disputes">
          <ul className="list-disc pl-5 space-y-2">
            <li>Raise the issue directly in the booking thread first; most disputes resolve there.</li>
            <li>If unresolved after 7 days, escalate through Support Center with evidence (messages, booking record, proof of attendance or delivery).</li>
            <li>We may hold disputed funds while we review the recorded evidence, and our decision on platform-held funds is final for release purposes but does not limit your legal rights.</li>
            <li>These terms are governed by the laws of the Republic of South Africa.</li>
          </ul>
        </Section>

        <Section title="11. Contact">
          <p>
            Bookings and disputes: <a href="mailto:support@metrocitytrading.co.za" className="text-purple-400 hover:text-purple-300">support@metrocitytrading.co.za</a>.
            Agencies should also read the{" "}
            <Link to="/agency-partnership-agreement" className="text-purple-400 hover:text-purple-300">Agency Partnership Agreement</Link>.
          </p>
        </Section>
      </main>
    </div>
  );
};

export default CollaborationTerms;
